import { computed, inject, Service, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { EasyEnvironment } from '@shared-types';
import { Observable, defer, from, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';

/**
 * The running build and the way to a newer one.
 *
 * Shared by the سایر page's «راه اندازی مجدد» and the desktop side menu's refresh
 * button, so both print the same version line and neither re-enters a check the
 * other started.
 */
@Service()
export class AppUpdateService {
	private readonly environment = inject(EasyEnvironment);
	private readonly swUpdate = inject(SwUpdate);

	/** True while the worker is being asked for a newer build; keeps the button from re-entering. */
	readonly checkingForUpdate = signal(false);

	/**
	 * `Version x, Build y` — the CI-stamped build this app is running.
	 *
	 * The placeholders are substituted at release time, so a local or unstamped
	 * build has nothing worth showing and the line is left off entirely rather
	 * than printed with a literal `${VERSION}` in it.
	 */
	readonly versionLabel = computed(() => {
		const version = this.stamped(this.environment.version);
		const build = this.stamped(this.environment.build);

		if (!version) {
			return '';
		}
		return build ? `Version ${version}, Build ${build}` : `Version ${version}`;
	});

	reloadToLatest(): Observable<void> {
		return defer(() => {
			if (this.checkingForUpdate()) {
				return of(undefined);
			}

			this.checkingForUpdate.set(true);

			const updateCheck$ = this.swUpdate.isEnabled ? from(this.swUpdate.checkForUpdate()) : of(false);

			return updateCheck$.pipe(
				switchMap(hasUpdate =>
					hasUpdate ? from(this.swUpdate.activateUpdate()).pipe(map(() => undefined)) : of(undefined)
				),
				catchError(() =>
					// A missing worker or an offline check should not prevent a normal reload.
					of(undefined)
				),
				finalize(() => {
					// Keep the rotating sync icon visible briefly before the page reloads.
					setTimeout(() => window.location.reload(), 500);
				})
			);
		});
	}

	/** A CI placeholder that was never substituted is not a version. */
	private stamped(value: string | undefined): string {
		const trimmed = value?.trim() ?? '';
		return trimmed.startsWith('${') ? '' : trimmed;
	}
}
